import React, { useEffect, useState } from "react";
import { Share, Plus, X, Smartphone, MoreVertical } from "lucide-react";

const CLE_REFUS = "espace_installation_refusee";

const estInstallee = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const estIos = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

/* L'invitation à poser 1000 ARTS sur l'écran d'accueil.

   Le piège : `beforeinstallprompt` n'existe QUE sur Chrome et Edge. Sur iPhone, sur
   Firefox et sur Safari de bureau, aucun événement n'arrive jamais — si on attend le
   bouton, personne ne voit rien et personne n'installe l'app. On affiche donc toujours la
   carte, avec le geste qui correspond au navigateur du visiteur.

   Un refus est mémorisé : on ne redemande plus. */
export const InstallerApp = ({ dejaInstallee = false }) => {
  const [invite, setInvite] = useState(null);
  const [visible, setVisible] = useState(false);
  const [guide, setGuide] = useState(false);

  useEffect(() => {
    // `dejaInstallee` : le serveur sait que la famille a l'app sur un téléphone ; ouverte
    // ici dans un navigateur (lien d'un e-mail, ordinateur), on ne la relance pas.
    if (dejaInstallee || estInstallee() || localStorage.getItem(CLE_REFUS)) return undefined;
    setVisible(true);
    const capter = (e) => {
      e.preventDefault();
      setInvite(e);
    };
    window.addEventListener("beforeinstallprompt", capter);
    const installee = () => setVisible(false);
    window.addEventListener("appinstalled", installee);
    return () => {
      window.removeEventListener("beforeinstallprompt", capter);
      window.removeEventListener("appinstalled", installee);
    };
  }, [dejaInstallee]);

  const refuser = () => {
    localStorage.setItem(CLE_REFUS, "1");
    setVisible(false);
  };

  /* UN seul bouton pour tout le monde. Quand le navigateur sait installer tout seul
     (Chrome, Edge, Samsung sur Android), il installe en un tap. Sinon — iPhone, où Apple
     ne laisse AUCUN site installer à la place de l'utilisateur, et quelques navigateurs
     Android — le même bouton ouvre le mode d'emploi en deux gestes. */
  const installer = async () => {
    if (!invite) {
      setGuide(true);
      return;
    }
    invite.prompt();
    const { outcome } = await invite.userChoice;
    if (outcome === "accepted") setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="card-flat flex items-start gap-3" data-testid="espace-installer"
      style={{ background: "var(--primary-tint)", borderColor: "transparent" }}>
      <Smartphone size={18} className="shrink-0 mt-0.5" style={{ color: "var(--primary)" }} />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">Garde 1000 ARTS sur ton téléphone</div>
        <p className="text-xs text-[var(--muted)] mt-1">
          Une icône sur l'écran d'accueil, et ton espace s'ouvre en un tap, sans passer
          par le navigateur.
        </p>

        <button onClick={installer} className="btn-primary mt-3 text-sm"
          data-testid="espace-installer-lancer">
          Installer l'application
        </button>

        {guide && (estIos() ? (
          /* iPhone récent (iOS 26) : « Partager » est derrière les trois points « ··· », et
             « Sur l'écran d'accueil » derrière « Afficher plus » au bas de la fenêtre
             (constaté par l'école le 16.10.2026). Les deux chemins sont donnés. */
          <ol className="text-xs mt-3 space-y-2 list-decimal pl-4" data-testid="espace-installer-ios">
            <li>Touche les trois petits points <strong>···</strong> à droite de l'adresse, puis{" "}
              <Share size={12} className="inline -mt-0.5" /> <strong>Partager</strong>. Sur un iPhone
              plus ancien : touche directement <Share size={12} className="inline -mt-0.5" />{" "}
              <strong>Partager</strong> en bas de Safari.</li>
            <li>Dans la fenêtre qui s'ouvre, tout en bas, touche <strong>Afficher plus</strong>{" "}
              (le rond avec une flèche vers le bas). Sur un iPhone plus ancien : fais défiler
              la liste.</li>
            <li>Choisis <Plus size={12} className="inline -mt-0.5" /> <strong>Sur l'écran
              d'accueil</strong>, laisse « Ouvrir comme app web » allumé, puis touche{" "}
              <strong>Ajouter</strong>.</li>
          </ol>
        ) : (
          <ol className="text-xs mt-3 space-y-2 list-decimal pl-4" data-testid="espace-installer-autre">
            <li>Ouvre le menu <MoreVertical size={12} className="inline -mt-0.5" /> de ton
              navigateur (en haut à droite).</li>
            <li>Choisis <strong>Installer l'application</strong> ou{" "}
              <strong>Ajouter à l'écran d'accueil</strong>.</li>
          </ol>
        ))}
      </div>
      <button onClick={refuser} aria-label="Ne plus proposer"
        className="shrink-0 text-[var(--muted)]" data-testid="espace-installer-fermer">
        <X size={16} />
      </button>
    </div>
  );
};
